/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Modal, Row, Spinner } from "react-bootstrap";
import { api, ApiError } from "core/api";
import { Button, Table, SvgIcon, Input, Alert } from "core/components";
import { MaterialRequestStatus, ProjectFormData } from "core/schema";
import moment from "moment";
import {
  admin_material_request_path,
  approve_material_request_admin_project_path,
  delete_material_request_admin_project_path,
  print_request_admin_material_request_path,
  reject_material_request_admin_project_path
} from "routes";
import ProjectMaterialRequest from "./ProjectMaterialRequest";
import { useForm } from "react-hook-form";

type ProjectCostBudgetProps = {
  project: ProjectFormData;
};

const ProjectMaterialRequests = ({
  project,
  loading,
  material_requests,
  getMaterialRequests,
  can_approve_reject,
  can_delete
}: ProjectCostBudgetProps): JSX.Element => {
  const {
    control,
    formState: { isSubmitting, isValidating },
    handleSubmit,
    reset,
    getValues,
    setValue
  } = useForm({
    mode: "onChange"
  });
  const [isApproving, setIsApproving] = React.useState(false);
  const [materialRequests, setMaterialRequests] = React.useState(material_requests);
  const [materialRequest, setMaterialRequest] = React.useState(null);
  const [showMaterialRequestModal, setShowMaterialRequestModal] = React.useState(false);
  const [showApproveMaterialRequestModal, setShowApproveMaterialRequestModal] = React.useState(false);
  const [showRejectMaterialRequestModal, setShowRejectMaterialRequestModal] = React.useState(false);
  const [showDeleteMaterialRequestModal, setShowDeleteMaterialRequestModal] = React.useState(false);
  const [currentMaterialRequest, setCurrentMaterialRequest] = React.useState(null);
  const [formErrors, setFormErrors] = React.useState<Array<string>>([]);
  const [itemsForDestruction, setItemsForDestruction] = React.useState([]);
  const table = React.useRef(null);

  const columns = React.useMemo(
    () => [
      {
        id: "id",
        Header: "Id",
        accessor: "id"
      },
      {
        id: 1,
        Header: "MR No.",
        accessor: "code",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: (props) => {
          const status = I18n.t(`activerecord.attributes.material_request.statuses.${props.value}`);

          return (
            <div
              className={`d-flex justify-content-center badge badge--pill badge--${props.value.replace("_", "-")}`}
              title={status}
              data-bs-toggle="tooltip"
            >
              <p className="text-truncate">{status}</p>
            </div>
          );
        }
      },
      {
        Header: "Date Needed",
        accessor: "date_needed",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{moment(props.value).format("MM/DD/YYYY")}</p>
            </div>
          );
        }
      },
      {
        Header: "Date Closed",
        accessor: "date_closed",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{props.value ? moment(props.value).format("MM/DD/YYYY") : "-"}</p>
            </div>
          );
        }
      },
      {
        Header: "Requestor",
        accessor: "requested_by",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Approver",
        accessor: "approved_by",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{props.value || "-"}</p>
            </div>
          );
        }
      },
      {
        Header: "Actions",
        accessor: "",
        Cell: (props) => {
          const isOpen = props.row.values.status == MaterialRequestStatus.Open;
          const isForApproval = props.row.values.status == MaterialRequestStatus.ForApproval;

          return (
            <div className="d-flex text-center">
              <span title="View Material Request" className="d-flex" data-bs-toggle="tooltip">
                <a
                  className="button button--action button--action--success"
                  onClick={() => {
                    setMaterialRequest(props.row.original);
                    setShowMaterialRequestModal(!showMaterialRequestModal);
                  }}
                >
                  <SvgIcon name="view" className="h5" />
                </a>
              </span>
              {!isForApproval && (
                <span title="Print Material Request" className="d-flex" data-bs-toggle="tooltip">
                  <a
                    className="button button--action button--action--primary"
                    onClick={() => window.open(print_request_admin_material_request_path(props.row.original.id))}
                  >
                    <SvgIcon name="printer" className="h5" />
                  </a>
                </span>
              )}
              {can_approve_reject && isForApproval && (
                <>
                  <span title="Approve Material Request" className="d-flex" data-bs-toggle="tooltip">
                    <a
                      className="button button--action button--action-approve"
                      onClick={() => {
                        setCurrentMaterialRequest(props.row.original);
                        setShowApproveMaterialRequestModal(!showApproveMaterialRequestModal);
                      }}
                    >
                      <SvgIcon name="check-circle" className="h6" />
                    </a>
                  </span>
                  <span title="Reject Material Request" className="d-flex" data-bs-toggle="tooltip">
                    <a
                      className="button button--action button--action-danger"
                      onClick={() => {
                        setCurrentMaterialRequest(props.row.original);
                        setShowRejectMaterialRequestModal(!showRejectMaterialRequestModal);
                      }}
                    >
                      <SvgIcon name="times-circle" className="h6" />
                    </a>
                  </span>
                </>
              )}
              {can_delete && (isOpen || isForApproval) && (
                <span title="Delete Material Request" className="d-flex" data-bs-toggle="tooltip">
                  <a
                    className="button button--action button--action-danger"
                    onClick={() => {
                      setCurrentMaterialRequest(props.row.original);
                      setShowDeleteMaterialRequestModal(!showDeleteMaterialRequestModal);
                    }}
                  >
                    <SvgIcon name="delete" className="h6" />
                  </a>
                </span>
              )}
            </div>
          );
        }
      }
    ],
    []
  );

  const updateMaterialRequests = (materialRequests, updatedMaterialRequest) => {
    const updatedMaterialRequests = materialRequests.map((materialRequest) => {
      if (materialRequest.id == updatedMaterialRequest.id) {
        return { ...materialRequest, ...updatedMaterialRequest };
      }

      return materialRequest;
    });

    setMaterialRequests(updatedMaterialRequests);
  };

  const approveMaterialRequest = React.useCallback(async (materialRequests, materialRequestId) => {
    try {
      setIsApproving(true);
      const response = await api.put(approve_material_request_admin_project_path({ id: project.id }, { material_request_id: materialRequestId }));
      updateMaterialRequests(materialRequests, response.data);
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    } finally {
      setIsApproving(false);
    }
  }, []);

  const rejectMaterialRequest = React.useCallback(async (materialRequests, materialRequestId) => {
    try {
      const response = await api.put(reject_material_request_admin_project_path({ id: project.id }, { material_request_id: materialRequestId }));
      updateMaterialRequests(materialRequests, response.data);
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    }
  }, []);

  const deleteMaterialRequest = React.useCallback(async (materialRequests, materialRequestId) => {
    try {
      const response = await api.deleteItem(
        delete_material_request_admin_project_path({ id: project.id }, { material_request_id: materialRequestId })
      );
      setMaterialRequests(materialRequests.filter((materialRequest) => materialRequest.id != response.data.id));
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    }
  }, []);

  const withItemsForDestruction = (data) => {
    const { material_request_items } = data;

    return { ...data, material_request_items: [...material_request_items, ...itemsForDestruction] };
  };

  const save = async (data): Promise<unknown> => {
    try {
      await api.put(admin_material_request_path(data.id), {
        ...withItemsForDestruction(data)
      });
      setMaterialRequests(await getMaterialRequests());
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    } finally {
      setShowMaterialRequestModal(!showMaterialRequestModal);
    }
  };

  const onError = (data) => {
    console.error(data);
  };

  React.useEffect(() => {
    setMaterialRequests(material_requests);
  }, [JSON.stringify(material_requests)]);

  const loader = () => {
    return (
      <div className="d-flex justify-content-center align-items-center h-100 pt-lg-8 pb-lg-8">
        <Spinner animation="border" role="status" size="lg" variant="brand">
          <span className="visually-hidden">Loading Project Change Request Material Requests...</span>
        </Spinner>
      </div>
    );
  };

  const noContent = () => {
    return (
      <Container fluid={true} className="text-center pt-lg-8 pb-lg-8">
        <Row className="mb-8">
          <Col>
            <SvgIcon name="clipboard-list" className="display-icon text-gray" />
          </Col>
        </Row>
        <Row>
          <Col>
            <h5>No material requests.</h5>
          </Col>
        </Row>
      </Container>
    );
  };

  const dataFilter = () => {
    return (
      <Row className="mb-3">
        <Col>
          <Input placeholder="Search for Material Request Number" onChange={(e) => table.current.setFilter(1, e.target.value)} />
        </Col>
      </Row>
    );
  };

  const dataTable = () => {
    return (
      <Row>
        <Col>
          <Table columns={columns} data={materialRequests} ref={table} />
        </Col>
      </Row>
    );
  };

  const content = () => {
    return materialRequests.length > 0 ? (
      <>
        {dataFilter()}
        {dataTable()}
      </>
    ) : (
      noContent()
    );
  };

  return (
    <React.StrictMode>
      <Col>
        {formErrors && formErrors.length != 0 && (
          <Alert>
            {formErrors.map((error, index) => {
              return <div key={index}>{error}</div>;
            })}
          </Alert>
        )}
      </Col>
      {loading ? loader() : content()}
      <Modal dialogClassName="modal-dialog-centered modal-xl" show={showMaterialRequestModal}>
        <Modal.Header>{`Material Request: ${materialRequest?.code}`}</Modal.Header>
        <Modal.Body>
          <ProjectMaterialRequest
            materialRequest={materialRequest}
            control={control}
            reset={reset}
            getValues={getValues}
            setValue={setValue}
            setItemsForDestruction={setItemsForDestruction}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowMaterialRequestModal(!showMaterialRequestModal);
            }}
            icon="times"
            title="Close"
          />
          {[MaterialRequestStatus.Open, MaterialRequestStatus.ForApproval].includes(materialRequest?.status) && (
            <Button
              className="button--brand"
              icon="save"
              title="Save"
              disabled={isSubmitting || isValidating}
              onClick={() => {
                handleSubmit(async (data) => await save(data), onError)();
              }}
            />
          )}
        </Modal.Footer>
      </Modal>
      <Modal dialogClassName="modal-dialog-centered" show={showApproveMaterialRequestModal}>
        <Modal.Header>{`Approve Material Request: ${currentMaterialRequest?.code}`}</Modal.Header>
        <Modal.Body>{`Are you sure you want to approve material request: ${currentMaterialRequest?.code}?`}</Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowApproveMaterialRequestModal(!showApproveMaterialRequestModal);
            }}
            icon="times"
            title="Cancel"
          />
          <Button
            className="button--brand"
            icon="save"
            title="Yes, approve material request."
            disabled={isApproving}
            onClick={() => {
              approveMaterialRequest(materialRequests, currentMaterialRequest.id).then(() => {
                setShowApproveMaterialRequestModal(!showApproveMaterialRequestModal);
              });
            }}
          />
        </Modal.Footer>
      </Modal>
      <Modal dialogClassName="modal-dialog-centered" show={showRejectMaterialRequestModal}>
        <Modal.Header>{`Reject Material Request: ${currentMaterialRequest?.code}`}</Modal.Header>
        <Modal.Body>{`Are you sure you want to reject material request: ${currentMaterialRequest?.code}?`}</Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowRejectMaterialRequestModal(!showRejectMaterialRequestModal);
            }}
            icon="times"
            title="Cancel"
          />
          <Button
            className="button--danger"
            icon="times-circle"
            title="Yes, reject material request."
            onClick={() => {
              rejectMaterialRequest(materialRequests, currentMaterialRequest.id).then(() => {
                setShowRejectMaterialRequestModal(!showRejectMaterialRequestModal);
              });
            }}
          />
        </Modal.Footer>
      </Modal>
      <Modal dialogClassName="modal-dialog-centered" show={showDeleteMaterialRequestModal}>
        <Modal.Header>{`Delete Material Request: ${currentMaterialRequest?.code}`}</Modal.Header>
        <Modal.Body>{`Are you sure you want to delete material request: ${currentMaterialRequest?.code}?`}</Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowDeleteMaterialRequestModal(!showDeleteMaterialRequestModal);
            }}
            icon="times"
            title="Cancel"
          />
          <Button
            className="button--danger"
            icon="delete"
            title="Yes, delete material request."
            onClick={() => {
              deleteMaterialRequest(materialRequests, currentMaterialRequest.id).then(() => {
                setShowDeleteMaterialRequestModal(!showDeleteMaterialRequestModal);
              });
            }}
          />
        </Modal.Footer>
      </Modal>
    </React.StrictMode>
  );
};

export default ProjectMaterialRequests;
